import { InsightAppearances, InsightState, InsightTypes } from '@core/insights';
import { useUserData } from '@web/features/users/use-user-data';
import { NextRouter } from 'next/router';
import * as React from 'react';
import { parseInsightUrl } from './insights-url';
import { TMApi } from '@web/services/tmapi';

const initialState: InsightState = {
  status: 'IDLE',
  timeframe: 'overview',
  appearance: 'calendar',
  type: 'hope',
  date: null,
  week: null,
  month: null,
  year: null,
  dateObj: new Date(),
  wellnessChecks: null,
};

const getCustomStyles = (type: InsightTypes) => {
  switch (type) {
    case 'spiritual':
    case 'physical': {
      return {
        url: `wellness-bg.svg`,
        color: '#31973B',
        backgroundColor: '#60C6FF',
      };
    }
    case 'hope': {
      return { url: `hope-check/hope-5.jpg`, color: '#60C6FF' };
    }
    case 'emotional': {
      return { url: `insights-bg.png`, color: '#60C6FF' };
    }

    default: {
      throw new Error('Invalid type');
    }
  }
};

/**
 * # useInsightsData
 * @packageDescription
 */
export function useInsightsData(router: NextRouter) {
  const [{ userData }] = useUserData();

  const [state, setState] = React.useState<InsightState>(initialState);

  const onError = (error: Error) =>
    setState((prev) => ({ ...prev, status: 'ERROR', error }));

  const clearError = () => setState((prev) => ({ ...prev, status: 'PENDING' }));

  const setAppearance = (appearance: InsightAppearances) =>
    setState(({ ...prev }) => ({ ...prev, appearance }));

  const setType = (type: InsightTypes) =>
    setState(({ ...prev }) => ({ ...prev, type }));

  const shouldFetchData = userData && state.status === 'IDLE';
  const uid = userData?.created_by;

  React.useEffect(() => {
    setState((prev) => ({ ...prev, ...parseInsightUrl(router.query) }));
  }, [router.query]);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setState((prev) => ({ ...prev, status: 'PENDING' }));

        const tmapi = new TMApi();
        const userWellnessCollection = await tmapi.getWellnessChecks();

        setState((prev) => ({
          ...prev,
          status: 'SUCCESS',
          wellnessChecks: userWellnessCollection,
        }));
      } catch (error) {
        onError(error as Error);
      }
    };

    if (shouldFetchData) {
      fetchData();
    }
  }, [shouldFetchData, uid]);

  return [
    state,
    { onError, clearError, getCustomStyles, setAppearance, setType },
  ] as const;
}
